import React, { useState, useEffect, useMemo } from 'react';
import { useDashboard } from './DashboardContext';
import { quotesApi, tradingPlanApi } from '../../services/api';
import useVisiblePolling from '../../hooks/useVisiblePolling';
import NudgeBanner from './NudgeBanner';
import {
  DollarSign,
  Target,
  TrendingUp,
  ClipboardCheck,
  Clock,
  Sparkles,
  Calendar
} from 'lucide-react';

const DashboardHome = () => {
  const { stats, trades, countdown, setActivePage, refreshTrades } = useDashboard();
  const [dailyQuote, setDailyQuote] = useState({
    quote: "Loading...",
    author: ""
  });
  const [habitLogs, setHabitLogs] = useState([]);
  const [tradingPlanRules, setTradingPlanRules] = useState([]);

  const fetchHabitData = async () => {
    try {
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1;
      const [rules, logs] = await Promise.all([
        tradingPlanApi.getRules(),
        tradingPlanApi.getMonthlyHabitLogs(currentYear, currentMonth)
      ]);
      setTradingPlanRules(rules);
      setHabitLogs(logs);
    } catch (error) {
      console.error('Failed to fetch habit data:', error);
    }
  };

  useEffect(() => {
    const fetchDailyQuote = async () => {
      try {
        const quote = await quotesApi.getDaily();
        setDailyQuote(quote);
      } catch (error) {
        console.error('Failed to fetch daily quote:', error);
        setDailyQuote({
          quote: "Success is not final, failure is not fatal: it is the courage to continue that counts.",
          author: "Winston Churchill"
        });
      }
    };
    fetchDailyQuote();
    fetchHabitData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep today's stats (trades + habit score) fresh in the background.
  useVisiblePolling(async () => {
    await Promise.all([
      refreshTrades ? refreshTrades() : Promise.resolve(),
      fetchHabitData(),
    ]);
  }, { intervalMs: 30000 });

  // Recalculate habit scores based on CURRENT rules (same logic as PerformancePage)
  const recalculatedHabitScores = useMemo(() => {
    if (!tradingPlanRules || tradingPlanRules.length === 0 || !habitLogs || habitLogs.length === 0) {
      return { todayScore: null, monthlyScore: null };
    }
    
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;
    const today = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
    
    const currentRuleIds = new Set(tradingPlanRules.map(r => r.id));
    const totalRules = tradingPlanRules.length;
    
    let todayScore = null;
    let validDayScores = [];
    
    habitLogs.forEach(log => {
      if (!log.entries || log.entries.length === 0) return;
      
      // Filter entries to only include current rules
      const relevantEntries = log.entries.filter(entry => currentRuleIds.has(entry.rule_id));
      
      // If no entries match current rules, skip this day
      if (relevantEntries.length === 0) return;
      
      // Count how many current rules were followed
      const followedCount = relevantEntries.filter(e => e.followed).length;
      
      // Recalculate score based on TOTAL current rules
      const recalculatedScore = (followedCount / totalRules) * 100;
      
      // Only count days with score > 0 in monthly average
      if (recalculatedScore > 0) {
        validDayScores.push(recalculatedScore);
      }
      
      // Check if this is today
      if (log.date === today) {
        todayScore = recalculatedScore;
      }
    });
    
    // Calculate monthly average from valid days only
    const monthlyScore = validDayScores.length > 0 
      ? validDayScores.reduce((sum, s) => sum + s, 0) / validDayScores.length
      : null;
    
    return { todayScore, monthlyScore };
  }, [habitLogs, tradingPlanRules]);

  // Calculate current month stats (same logic as PerformancePage)
  const currentMonthStats = useMemo(() => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;

    // Filter trades for current month
    const monthlyTrades = trades.filter(trade => {
      const tradeDate = trade.date?.split('T')[0];
      if (!tradeDate) return false;
      const [year, month] = tradeDate.split('-').map(Number);
      return year === currentYear && month === currentMonth;
    });

    if (monthlyTrades.length === 0) {
      return {
        totalPL: 0,
        winRate: 0,
        wins: 0,
        losses: 0,
        avgRR: 0
      };
    }

    const totalPL = monthlyTrades.reduce((sum, trade) => {
      const plValue = parseFloat(trade.profitLoss || 0);
      const adjustedPL = trade.outcome === 'Loss' ? -Math.abs(plValue) : Math.abs(plValue);
      return sum + adjustedPL;
    }, 0);

    const wins = monthlyTrades.filter(t => t.outcome === 'Win').length;
    const losses = monthlyTrades.filter(t => t.outcome === 'Loss').length;
    const totalTrades = wins + losses;
    const winRate = totalTrades > 0 ? ((wins / totalTrades) * 100).toFixed(1) : 0;

    let totalRR = 0;
    let rrCount = 0;
    monthlyTrades.forEach(trade => {
      const entry = parseFloat(trade.entry || 0);
      const sl = parseFloat(trade.stopLoss || 0);
      const tp = parseFloat(trade.takeProfit || 0);
      if (entry && sl && tp) {
        const risk = Math.abs(entry - sl);
        const reward = Math.abs(tp - entry);
        if (risk > 0) {
          totalRR += reward / risk;
          rrCount++;
        }
      }
    });
    const avgRR = rrCount > 0 ? (totalRR / rrCount).toFixed(2) : 0;

    return {
      totalPL: totalPL.toFixed(2),
      winRate,
      wins,
      losses,
      avgRR
    };
  }, [trades]);

  // Get current month name
  const currentMonthName = new Date().toLocaleString('default', { month: 'long' });

  const statsData = [
    {
      icon: DollarSign,
      label: 'Total P&L',
      value: `$${currentMonthStats.totalPL >= 0 ? '' : '-'}${Math.abs(currentMonthStats.totalPL).toLocaleString()}`,
      color: currentMonthStats.totalPL >= 0 ? '#10b981' : '#ef4444',
      iconBg: currentMonthStats.totalPL >= 0 ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
    },
    {
      icon: Target,
      label: 'Win Rate',
      value: `${currentMonthStats.winRate}%`,
      subtitle: `${currentMonthStats.wins}W / ${currentMonthStats.losses}L`,
      color: '#ef4444',
      iconBg: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
    },
    {
      icon: TrendingUp,
      label: 'Avg R:R',
      value: `${currentMonthStats.avgRR}R`,
      color: '#8b5cf6',
      iconBg: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
    },
    {
      icon: ClipboardCheck,
      label: 'Habit Score',
      value: recalculatedHabitScores.todayScore !== null || recalculatedHabitScores.monthlyScore !== null ? null : 'Not tracked',
      customValue: (recalculatedHabitScores.todayScore !== null || recalculatedHabitScores.monthlyScore !== null) ? (
        <div className="habit-score-value">
          <div className="habit-today-score">
            <span className="habit-score-label">Today:</span>
            <span>{recalculatedHabitScores.todayScore !== null ? `${Number(recalculatedHabitScores.todayScore).toFixed(1)}%` : '-'}</span>
          </div>
          <div className="habit-month-score">
            <span className="habit-score-label">Month:</span>
            <span>{recalculatedHabitScores.monthlyScore !== null ? `${Number(recalculatedHabitScores.monthlyScore).toFixed(1)}%` : '-'}</span>
          </div>
        </div>
      ) : null,
      color: '#8b5cf6',
      iconBg: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
      isHabitCard: true
    },
  ];

  return (
    <div className="dashboard-home-wrapper">
      <div className="welcome-header">
        <h1 className="welcome-title">Welcome Back!</h1>
        <p className="welcome-subtitle">Here&apos;s your trading overview</p>
      </div>

      <NudgeBanner />

      <div className="motivation-card">
        <div className="motivation-header">
          <Sparkles size={20} className="motivation-icon" />
          <h3 className="motivation-title">Motivation of the Day</h3>
        </div>
        <blockquote className="motivation-quote">
          &ldquo;{dailyQuote.quote}&rdquo;
        </blockquote>
        <p className="motivation-author">— {dailyQuote.author}</p>
      </div>

      <section className="performance-section">
        <div className="section-heading-with-icon">
          <Calendar size={20} />
          <h2 className="section-heading">{currentMonthName} Performance</h2>
        </div>
        <div className="stats-grid-full">
          {statsData.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div 
                key={index} 
                className={`stat-card-new clickable ${stat.isHabitCard ? 'habit-score-card' : ''}`}
                onClick={() => setActivePage('Performance')}
                style={{ cursor: 'pointer' }}
              >
                <div className="stat-card-content">
                  <div className="stat-header">
                    <span className="stat-label-new">{stat.label}</span>
                    <div className="stat-icon-new" style={{ background: stat.iconBg }}>
                      <Icon size={18} />
                    </div>
                  </div>
                  {stat.customValue ? (
                    <div className="stat-value-new" style={{ color: stat.color }}>
                      {stat.customValue}
                    </div>
                  ) : (
                    <div className="stat-value-new" style={{ color: stat.color }}>
                      {stat.value}
                    </div>
                  )}
                  {stat.subtitle && (
                    <div className="stat-subtitle-new">{stat.subtitle}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <div 
        className="trading-session-card clickable" 
        onClick={() => setActivePage('Live Stream')}
        style={{ cursor: 'pointer' }}
      >
        <div className="session-content">
          <div className="session-left">
            <div className="session-icon">
              <Clock size={24} />
            </div>
            <div className="session-info">
              <p className="session-label">Next Trading Session</p>
              <h3 className="session-title">Coach CJ&apos;s Live Stream</h3>
            </div>
          </div>
          <div className="session-right">
            <div className="session-countdown">
              <p className="countdown-label">Starts in</p>
              <div className="countdown-timer">{countdown}</div>
              <p className="countdown-schedule">9:20 AM ET • Mon-Fri</p>
            </div>
          </div>
        </div>
      </div>

      <div className="cta-buttons">
        <button className="cta-button" onClick={() => setActivePage('Performance')}>
          Log New Trade
        </button>
        <button className="cta-button" onClick={() => setActivePage('Courses')}>
          Continue Learning
        </button>
        <button className="cta-button" onClick={() => setActivePage('Community')}>
          Join Community Chat
        </button>
      </div>
    </div>
  );
};

export default DashboardHome;